import React from "react";

import { css } from "@emotion/css";
import MenuItem from "@mui/material/MenuItem";
import { CellContext } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import {
  PractitionerBilling,
  PractitionerBillingStatus,
  practitionerBillingStatusTranslation,
} from "@/models/PractitionerBillingModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { DropDownSelect } from "@/uiKit/atoms/DropDownSelect.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type StatusColumnProps = CellContext<PractitionerBilling, PractitionerBillingStatus>;

const STATUS_OPTIONS: PractitionerBillingStatus[] = ["to_process", "processed", "error"];

export const StatusColumn: React.FC<StatusColumnProps> = ({ renderValue, row }) => {
  const status = renderValue();
  const { t } = useTranslation();
  const me = Queries.user.usePractitionerMe({});
  const changeStatus = Queries.billing.usePractitionerBillingStatus();

  if (!status) {
    return null;
  }

  if (me.qualification === "nurse") {
    return <Typography variant="body">{t(practitionerBillingStatusTranslation(status))}</Typography>;
  }
  return (
    <DropDownSelect
      id={`account-billing-status-${row.original.id}`}
      value={status}
      disabled={changeStatus.isPending}
      onChange={value =>
        changeStatus.mutate({
          billingId: row.original.id,
          status: value.target.value as PractitionerBillingStatus,
        })
      }
    >
      {STATUS_OPTIONS.map(status => (
        <MenuItem key={status} id={`account-billing-status-${row.original.id}-option-${status}`} value={status}>
          {t(practitionerBillingStatusTranslation(status))}
        </MenuItem>
      ))}
    </DropDownSelect>
  );
};

export const StatusColumnHeader: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Typography variant="bodyLarge" className={css({ fontWeight: "bold" })}>
      {t("pages.practitionerBilling.status")}
    </Typography>
  );
};
